'use client'

import React, { forwardRef, useState, useRef, useEffect } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon, ChevronDown, Check } from 'lucide-react'
import { DropdownPortal, useDropdownPosition } from './DropdownPortal'

// Tipos base do select
type SelectSize = 'sm' | 'md' | 'lg'
type SelectVariant = 'default' | 'error' | 'success'

// Interface para opções
export interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
  icon?: LucideIcon
}

// Interface para aparência do select
interface SelectAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  placeholderColor?: string
}

interface SelectProps {
  // Opções
  options: SelectOption[]
  value?: string | number
  onChange?: (value: string | number) => void
  
  // Configuração visual
  variant?: SelectVariant
  size?: SelectSize
  
  // Estados
  error?: boolean
  success?: boolean
  disabled?: boolean
  
  // Ícones
  leftIcon?: LucideIcon
  
  // Labels e mensagens
  label?: string
  helperText?: string
  errorMessage?: string
  placeholder?: string
  
  // Customização
  appearance?: SelectAppearance
  
  // Layout
  fullWidth?: boolean
  className?: string
  
  // Comportamento
  searchable?: boolean
  clearable?: boolean
  required?: boolean
}

// Presets de variantes (reutilizando do Input)
const variantPresets: Record<SelectVariant, SelectAppearance> = {
  default: {
    backgroundColor: colors.background.white,
    textColor: colors.text.primary,
    borderColor: colors.border.primary,
    placeholderColor: colors.text.tertiary,
  },
  error: {
    backgroundColor: colors.status.error.background,
    textColor: colors.status.error.text,
    borderColor: colors.status.error.text,
    placeholderColor: colors.text.tertiary,
  },
  success: {
    backgroundColor: colors.status.success.background,
    textColor: colors.status.success.text,
    borderColor: colors.status.success.text,
    placeholderColor: colors.text.tertiary,
  },
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(({
  options,
  value,
  onChange,
  variant = 'default',
  size = 'md',
  error = false,
  success = false,
  disabled = false,
  leftIcon: LeftIcon,
  label,
  helperText,
  errorMessage,
  placeholder = 'Selecione uma opção',
  appearance,
  fullWidth = false,
  className,
  searchable = false,
  clearable = false,
  required = false,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const selectRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownPosition = useDropdownPosition(selectRef, isOpen)

  // Determinar variante baseada nos estados
  const currentVariant = error ? 'error' : success ? 'success' : variant
  
  // Determinar aparência final
  const preset = variantPresets[currentVariant]
  const finalAppearance: SelectAppearance = {
    backgroundColor: appearance?.backgroundColor || (disabled ? colors.palette.gray[50] : preset.backgroundColor),
    textColor: appearance?.textColor || (disabled ? colors.palette.gray[400] : preset.textColor),
    borderColor: appearance?.borderColor || (disabled ? colors.palette.gray[200] : preset.borderColor),
    placeholderColor: appearance?.placeholderColor || preset.placeholderColor,
  }

  // Classes de tamanho
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  }

  // Encontrar opção selecionada
  const selectedOption = options.find(option => option.value === value)

  // Filtrar opções baseado na busca
  const filteredOptions = searchable 
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // The dropdown lives in a portal under document.body — it must count
      // as "inside" for the outside-click check.
      if (
        selectRef.current && !selectRef.current.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus no input de busca quando abre
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  const handleSelect = (option: SelectOption) => {
    if (!option.disabled) {
      onChange?.(option.value)
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.('')
  }

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  // Classes base
  const baseClasses = 'border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary cursor-pointer'
  
  // Classes do container
  const containerClasses = cn(
    'relative',
    fullWidth ? 'w-full' : 'w-auto'
  )

  // Classes do select
  const selectClasses = cn(
    baseClasses,
    'flex items-center gap-2',
    sizes[size],
    fullWidth && 'w-full',
    LeftIcon && 'pl-10',
    'pr-10', // Sempre tem espaço para chevron
    disabled && 'cursor-not-allowed',
    className
  )

  // Estilos inline
  const selectStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    color: selectedOption ? finalAppearance.textColor : finalAppearance.placeholderColor,
    borderColor: finalAppearance.borderColor,
  }

  // Classes dos ícones
  const iconClasses = 'absolute top-1/2 transform -translate-y-1/2 w-5 h-5'
  const leftIconClasses = cn(iconClasses, 'left-3')
  const rightIconClasses = cn(iconClasses, 'right-3')

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium" style={{ color: finalAppearance.textColor }}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select Container */}
      <div className={containerClasses} ref={selectRef}>
        {/* Select Input */}
        <div
          ref={ref}
          className={selectClasses}
          style={selectStyles}
          onClick={handleToggle}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {/* Left Icon */}
          {LeftIcon && (
            <LeftIcon 
              className={leftIconClasses}
              style={{ color: finalAppearance.placeholderColor }}
            />
          )}

          {/* Value or Placeholder */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {selectedOption?.icon && (
              <selectedOption.icon className="w-4 h-4" />
            )}
            <span className="truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </div>

          {/* Right Icons */}
          <div className="flex shrink-0 items-center gap-1">
            {clearable && selectedOption && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <span className="text-xs">×</span>
              </button>
            )}
            <ChevronDown 
              className={cn(
                'w-4 h-4 transition-transform',
                isOpen && 'rotate-180'
              )}
              style={{ color: finalAppearance.placeholderColor }}
            />
          </div>
        </div>

        {/* Dropdown — portal on document.body so no Modal/overflow clips it */}
        {isOpen && (
          <DropdownPortal
            position={dropdownPosition}
            portalRef={dropdownRef}
            className="bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden"
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-2 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
            )}

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto" role="listbox">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchTerm ? 'Nenhuma opção encontrada' : 'Sem opções disponíveis'}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value
                  const IconComponent = option.icon

                  return (
                    <div
                      key={option.value}
                      className={cn(
                        'px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors',
                        isSelected && 'bg-brand-primary/10 text-brand-primary',
                        option.disabled && 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={() => handleSelect(option)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="w-4 h-4" />}
                        <span className="truncate">{option.label}</span>
                      </div>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  )
                })
              )}
            </div>
          </DropdownPortal>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {(helperText || errorMessage) && (
        <div className="text-sm">
          {errorMessage ? (
            <span style={{ color: colors.status.error.text }}>
              {errorMessage}
            </span>
          ) : (
            <span style={{ color: colors.text.secondary }}>
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

Select.displayName = 'Select'
